import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { X } from 'lucide-react';

/**
 * Iter-46at: GDPR + ePrivacy cookie consent banner.
 *
 * Behaviour:
 *   • Shows on first visit until the user chooses (Accept all / Essential only / Save preferences).
 *   • Persists choice in localStorage `ss_cookie_consent` as JSON:
 *       { version: 1, timestamp: <ISO>, choice: 'all' | 'essential' | 'custom',
 *         categories: { necessary: true, functional: bool, analytics: bool } }
 *   • Honours the Global Privacy Control (`navigator.globalPrivacyControl`) — if
 *     the browser sends the GPC signal we auto-store an "essential" choice and
 *     suppress the banner.
 *   • Footer "Cookie preferences" button re-opens the banner in Manage mode
 *     via a `ss:reopen-cookie-prefs` custom event.
 */

const STORAGE_KEY = 'ss_cookie_consent';
const REOPEN_KEY = 'ss_cookie_consent_reopen';
const CURRENT_VERSION = 1;

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const writeConsent = (choice, categories) => {
  const payload = {
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    choice,
    categories,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) { /* private-mode fallback: consent applies for the session only */ }
  // Emit an event so any listeners (e.g., analytics loader) can react.
  window.dispatchEvent(new CustomEvent('ss:cookie-consent-changed', { detail: payload }));
};

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [manageMode, setManageMode] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  const openBanner = useCallback((mode = 'summary') => {
    setManageMode(mode === 'manage');
    // Seed toggles from persisted preferences when re-opening.
    const existing = readConsent();
    if (existing?.categories) {
      setFunctional(!!existing.categories.functional);
      setAnalytics(!!existing.categories.analytics);
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    // Honour Global Privacy Control — auto-store an essential-only choice and suppress the banner.
    const gpc = typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
    const existing = readConsent();
    if (existing) return; // user already chose
    if (gpc) {
      writeConsent('essential', { necessary: true, functional: false, analytics: false });
      return;
    }
    // First visit — show banner after a short beat so it doesn't fight with page load.
    const t = setTimeout(() => openBanner('summary'), 400);
    return () => clearTimeout(t);
  }, [openBanner]);

  useEffect(() => {
    // Re-open handler — triggered by the footer "Cookie preferences" button.
    const onReopen = () => {
      try { localStorage.removeItem(REOPEN_KEY); } catch (_) { /* noop */ }
      openBanner('manage');
    };
    window.addEventListener('ss:reopen-cookie-prefs', onReopen);
    // Storage flag path (for cross-tab reopen requests).
    if (typeof localStorage !== 'undefined' && localStorage.getItem(REOPEN_KEY)) {
      onReopen();
    }
    return () => window.removeEventListener('ss:reopen-cookie-prefs', onReopen);
  }, [openBanner]);

  const acceptAll = () => {
    writeConsent('all', { necessary: true, functional: true, analytics: true });
    setVisible(false);
  };

  const essentialOnly = () => {
    writeConsent('essential', { necessary: true, functional: false, analytics: false });
    setVisible(false);
  };

  const savePreferences = () => {
    writeConsent('custom', { necessary: true, functional, analytics });
    setVisible(false);
  };

  useEffect(() => {
    // Iter-52c: while the banner is visible, reserve vertical space at the
    // bottom of the document so no page CTA (Login/Register submit, dialog
    // buttons, etc.) sits under the banner and becomes unclickable. Cleared
    // on hide so we don't leak padding into a clean state.
    if (!visible) {
      document.body.style.paddingBottom = '';
      document.body.style.removeProperty('--ss-cookie-banner-h');
      return undefined;
    }
    // 220px covers the tallest state (Manage mode with the 3 category
    // toggles expanded on a narrow viewport). Overshooting a few pixels
    // is invisible to the user because the banner already occupies that
    // space visually — we just need the flowing document to acknowledge it.
    document.body.style.paddingBottom = '220px';
    document.body.style.setProperty('--ss-cookie-banner-h', '220px');
    return () => {
      document.body.style.paddingBottom = '';
      document.body.style.removeProperty('--ss-cookie-banner-h');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-2 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <Card className="max-w-3xl mx-auto p-4 sm:p-5 shadow-lg border pointer-events-auto bg-background/95 backdrop-blur-md">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3
              id="cookie-consent-title"
              className="text-base font-semibold tracking-tight"
            >
              Cookies on StudioScheduler
            </h3>
            <p className="text-sm text-muted-foreground mt-1.5">
              We use strictly-necessary cookies to run the site (login, security, checkout). With
              your consent we also use functional cookies (remembering your preferences) and
              privacy-respecting analytics. Read our{' '}
              <Link to="/cookies" className="text-primary hover:opacity-80">
                Cookie Policy
              </Link>{' '}
              and <Link to="/privacy" className="text-primary hover:opacity-80">Privacy Policy</Link>.
            </p>

            {manageMode && (
              <div className="mt-4 space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/40">
                  <Checkbox
                    id="cookie-necessary"
                    checked
                    disabled
                    aria-label="Strictly necessary cookies — always on"
                  />
                  <div className="flex-1">
                    <label htmlFor="cookie-necessary" className="text-sm font-medium cursor-not-allowed">
                      Strictly necessary <span className="text-xs text-muted-foreground">(always on)</span>
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Authentication, CSRF protection, and consent storage. Required for the site to work.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border">
                  <Checkbox
                    id="cookie-functional"
                    checked={functional}
                    onCheckedChange={(v) => setFunctional(v === true)}
                    data-testid="cookie-functional-toggle"
                  />
                  <div className="flex-1">
                    <label htmlFor="cookie-functional" className="text-sm font-medium cursor-pointer">
                      Functional
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Recently viewed studios, preferred timezone, basket state.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border">
                  <Checkbox
                    id="cookie-analytics"
                    checked={analytics}
                    onCheckedChange={(v) => setAnalytics(v === true)}
                    data-testid="cookie-analytics-toggle"
                  />
                  <div className="flex-1">
                    <label htmlFor="cookie-analytics" className="text-sm font-medium cursor-pointer">
                      Analytics
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Privacy-respecting usage insights so we can improve the platform. Off by default.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={essentialOnly}
            className="text-muted-foreground hover:text-foreground -mt-1"
            aria-label="Close and accept essential cookies only"
            data-testid="cookie-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          {!manageMode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setManageMode(true)}
              data-testid="cookie-consent-manage-btn"
            >
              Manage
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={essentialOnly}
            data-testid="cookie-consent-essential-btn"
          >
            Essential only
          </Button>
          {manageMode ? (
            <Button
              size="sm"
              onClick={savePreferences}
              data-testid="cookie-consent-save-btn"
            >
              Save preferences
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={acceptAll}
              data-testid="cookie-consent-accept-all-btn"
            >
              Accept all
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CookieConsentBanner;
